// web/src/@shared/store/useAuthStore.js OR src/store/useAuthStore.js
import { create } from "zustand";
import {
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "../firebase/config";
import { db } from "../firebase/config";
import {
  collection,
  doc,
  addDoc,
  getDoc,
  setDoc,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { getCardImageByName } from "../utils/Utils";

export const useAuthStore = create((set) => ({
  user: null,
  userData: null,
  loading: true,
  quests: [],
  questLog: [],

  initAuth: () => {
    let unsubUser, unsubCards, unsubCharacters, unsubDecks, unsubRewards;
    let unsubQuests, unsubquestLog, unsubPurchases;

    onAuthStateChanged(auth, (firebaseUser) => {
      // 🔁 Clean up previous listeners
      if (unsubUser) unsubUser();
      if (unsubCards) unsubCards();
      if (unsubCharacters) unsubCharacters();
      if (unsubDecks) unsubDecks();
      if (unsubRewards) unsubRewards();
      if (unsubQuests) unsubQuests();
      if (unsubquestLog) unsubquestLog();
      if (unsubPurchases) unsubPurchases();

      if (firebaseUser) {
        set({ user: firebaseUser, loading: false });

        const userRef = doc(db, "users", firebaseUser.uid);
        const userCardsRef = collection(
          db,
          "users",
          firebaseUser.uid,
          "Infinity cards"
        );
        const userDecksRef = collection(db, "users", firebaseUser.uid, "decks");
        const userCharacterCardsRef = collection(
          db,
          "users",
          firebaseUser.uid,
          "Character cards"
        );
        const userRewardsRef = collection(
          db,
          "users",
          firebaseUser.uid,
          "rewards"
        );

        const userQuestsRef = collection(
          db,
          "users",
          firebaseUser.uid,
          "quests"
        );
        const userquestLogRef = collection(
          db,
          "users",
          firebaseUser.uid,
          "questLog"
        );

        const purchasesRef = collection(
          db,
          "users",
          firebaseUser.uid,
          "purchases"
        );

        unsubUser = onSnapshot(userRef, (snap) => {
          if (!snap.exists()) return set({ userData: null });
          const userBaseData = { ...snap.data(), id: snap.id };
          set((prev) => ({
            userData: { ...prev.userData, ...userBaseData },
          }));
        });

        unsubCards = onSnapshot(userCardsRef, (snap) => {
          const cards = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

          const updatedCards = [...cards].map((card) => {
            return { ...card, imageUrl: getCardImageByName(card.name) };
          });
          set((prev) => ({
            userData: { ...prev.userData, cards },
          }));
        });

        unsubCharacters = onSnapshot(userCharacterCardsRef, (snap) => {
          const nftCards = snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          set((prev) => ({
            userData: { ...prev.userData, nftCards },
          }));
        });

        unsubDecks = onSnapshot(userDecksRef, (snap) => {
          const decks = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          set((prev) => ({
            userData: { ...prev.userData, decks },
          }));
        });

        unsubRewards = onSnapshot(userRewardsRef, (snap) => {
          const rewards = snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          set((prev) => ({
            userData: { ...prev.userData, rewards },
          }));
        });

        unsubQuests = onSnapshot(userQuestsRef, (snap) => {
          const quests = snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          set((prev) => ({
            userData: { ...prev.userData, quests },
            quests, // store outside of userData too if needed elsewhere
          }));
        });

        unsubquestLog = onSnapshot(userquestLogRef, (snap) => {
          const questLog = snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          set((prev) => ({
            userData: { ...prev.userData, questLog },
            questLog,
          }));
        });

        unsubPurchases = onSnapshot(purchasesRef, (snap) => {
          const purchases = snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          set((prev) => ({
            userData: { ...prev.userData, purchases },
            purchases,
          }));
        });
      } else {
        set({ user: null, userData: null, loading: false });
      }
    });
  },

  login: async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true, message: "Login successful" };
    } catch (error) {
      console.error("Login Error:", error.message);
      return { success: false, message: error.message };
    }
  },

  signUp: async (
    username,
    email,
    password,
    onboardingData = false,
    setGameScreenLoading
  ) => {
    console.log(
      "signUp called with:",
      username,
      email,
      password,
      onboardingData
    );
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      const user = auth.currentUser;
      // 🧠 Create user document in Firestor
      const userRef = doc(db, "users", user.uid);
      const roles = ["player"];
      if (user) {
        setGameScreenLoading(true);
      }

      await setDoc(userRef, {
        username,
        name: onboardingData ? onboardingData.name : "",
        email,
        emailVerified: false,
        roles: onboardingData ? [...roles, onboardingData.role] : roles,
        record: {
          wins: 0,
          losses: 0,
          ties: 0,
          dnfs: 0,
        },
        imageUrl: "",
        world: "Void City",
        avatar: {
          image:
            "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2FinfintyOrb%2Fio-green.png?alt=media&token=feff4088-f1da-43f7-9c7c-c231fe45b240",
          name: "Infinty Orb",
          ws: "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2Fgreen.png?alt=media&token=b504236c-a42f-4a2c-a2bd-9742b489a398",
        },
        xp: 0,
        level: 1,
        completedTutorial: false,
        lastLogin: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      console.log("🚀 Calling issueInitialSignupRewards...");
      //   await issueInitialSignupRewards(user.uid);
      console.log("✅ issueInitialSignupRewards call complete.");
    } catch (error) {
      console.error("Sign Up Error:", error.message);
    }
  },

  logout: async () => {
    await signOut(auth);
    set({ user: null, userData: null });
  },
}));

const issueInitialSignupRewards = async (userId) => {
  try {
    console.log("🎁 Issuing signup rewards for:", userId);
    const rewardsRef = collection(db, "users", userId, "rewards");
    // Fetch available character cards
    const charactersSnap = await getDocs(collection(db, "Character cards"));
    const characterCards = charactersSnap.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      .map((card) => ({
        ...card,
        type: "character",
        _uuid: `${Date.now()}-${Math.random()}`,
      }))
      .filter(
        (card) =>
          card.name === "Zaporia" ||
          card.name === "Toshi" ||
          card.name === "Ashro"
      );

    // Fetch available infinity cards
    const infinitySnap = await getDocs(collection(db, "Infinity cards"));
    let infinityCards = infinitySnap.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
        _uuid: `${Date.now()}-${Math.random()}`,
      }))
      .filter((card) => card.finite !== "TRUE");

    // Defensive check
    if (characterCards.length === 0 || infinityCards.length < 40) {
      console.warn("⚠️ Not enough cards to issue signup rewards.");
      return;
    }

    // Shuffle helper
    const shuffleArray = (arr) => {
      const shuffled = [...arr];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    // 🎴 Shuffle and slice to 40 unique cards
    const shuffledInfinity = shuffleArray(infinityCards).slice(0, 40);

    // 🃏 Break into 4 card packs (10 each)
    const cardPacks = [];
    for (let i = 0; i < 4; i++) {
      const pack = shuffledInfinity.slice(i * 10, i * 10 + 10);
      cardPacks.push(pack);
    }

    // 1. Character card reward
    const getRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];
    await addDoc(rewardsRef, {
      type: "character card",
      issuedAt: Date.now(),
      claimed: false,
      reward: getRandom(characterCards),
      amount: 1,
    });

    // 2-5. Four card pack rewards
    for (let i = 0; i < 4; i++) {
      await addDoc(rewardsRef, {
        type: "card pack",
        issuedAt: Date.now(),
        claimed: false,
        reward: {
          pack: cardPacks[i],
        },
        amount: 1,
      });
    }

    // 6. Infinity tokens reward
    await addDoc(rewardsRef, {
      type: "infinity tokens",
      issuedAt: Date.now(),
      claimed: false,
      reward: { currency: "Infinity Tokens" },
      amount: 250,
    });

    console.log("✅ Signup rewards issued for user:", userId);
  } catch (e) {
    console.error("Error in issueInitialSignupRewards:", e);
  }
};
